import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const CreditsContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useCredits = () => {
  const context = useContext(CreditsContext);
  if (!context) {
    throw new Error('useCredits must be used within a CreditsProvider');
  }
  return context;
};

export const CreditsProvider = ({ children }) => {
  const { user } = useAuth();
  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState([]);
  const [downloadCost, setDownloadCost] = useState(5);

  const fetchCredits = useCallback(async () => {
    if (!user?.uid) {
      setCredits(0);
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/credits/${user.uid}`);
      setCredits(response.data.credits);
    } catch (error) {
      console.error('Failed to fetch credits:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchPackages = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/credits/packages`);
      setPackages(response.data.packages);
      setDownloadCost(response.data.download_cost);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    }
  }, []);

  useEffect(() => {
    fetchCredits();
    fetchPackages();
  }, [fetchCredits, fetchPackages]);

  const deductCredits = async (amount = downloadCost) => {
    if (!user?.uid) return { success: false, message: 'Not logged in' };
    
    try {
      const response = await axios.post(`${API}/credits/deduct`, {
        user_id: user.uid,
        amount
      });
      setCredits(response.data.credits);
      return { success: true, credits: response.data.credits };
    } catch (error) {
      if (error.response?.status === 402) {
        return { 
          success: false, 
          insufficient: true,
          current: error.response.data.detail.current,
          required: error.response.data.detail.required
        };
      }
      return { success: false, message: error.message };
    }
  };

  const addCredits = (amount) => {
    setCredits(prev => prev + amount);
  };

  const value = {
    credits,
    loading,
    packages,
    downloadCost,
    fetchCredits,
    deductCredits,
    addCredits,
    hasEnoughCredits: credits >= downloadCost
  };

  return (
    <CreditsContext.Provider value={value}>
      {children}
    </CreditsContext.Provider>
  );
};